import { IconCheckmark2Small } from 'central-icons/IconCheckmark2Small';
import { IconChevronDownSmall } from 'central-icons/IconChevronDownSmall';
import { IconCrossSmall } from 'central-icons/IconCrossSmall';
import { IconPlusSmall } from 'central-icons/IconPlusSmall';
import classNames from 'classnames';
import { useRef, useState } from 'react';

import {
  Menu,
  MenuButton,
  MenuList,
  MenuRadioGroup,
  MenuRadioItem,
} from '@revelio/ui';

import chipStyles from '../entity-chip.module.css';
import type { ChipSourceKey } from '../shortcut-source';
import styles from './RecentEntitiesPanel.module.css';

export type RecentEntity = {
  id: string;
  name: string;
  /** Needed to tell an entity apart from a same-numbered one at another level:
   *  a chip's id may be the bare entity id where the tree's is a path. */
  levelId: string;
};

/** Chips offered before the reader expands the rest. Mirrored by the e2e
 *  helpers in `support/filters/recents.ts`. */
const COLLAPSED_CHIP_COUNT = 6;

export type ChipSource = {
  key: ChipSourceKey;
  label: string;
  entities: RecentEntity[];
  /** Whether a chip can be dropped from the list it came from. */
  removable?: boolean;
  /**
   * Stay on offer with nothing to show. Recents do — a reader who has none yet
   * should still be able to switch across and be told so, rather than find the
   * idea missing from the picker entirely.
   */
  keepWhenEmpty?: boolean;
  /**
   * Still resolving. The source holds its place while it does, so the panel
   * doesn't open on a fallback and visibly change its mind a moment later.
   */
  isLoading?: boolean;
};

/**
 * The heading. Renders as plain text until there is a second source to switch
 * to — a trial plan's companies are the whole offer, and competitors only
 * exist for a company unit of analysis that resolves any.
 */
const SourceSelect = ({
  sources,
  activeKey,
  onChange,
}: {
  sources: ChipSource[];
  activeKey: ChipSourceKey;
  onChange: (key: ChipSourceKey) => void;
}) => {
  const activeLabel =
    sources.find((source) => source.key === activeKey)?.label ?? '';

  if (sources.length < 2) {
    return <div className={styles.heading}>{activeLabel}</div>;
  }

  return (
    <Menu>
      <MenuButton>
        <button
          type="button"
          className={classNames(styles.heading, styles.headingButton)}
          data-testid="recent-entities-source-trigger"
        >
          {activeLabel}
          <IconChevronDownSmall className={styles.headingChevron} />
        </button>
      </MenuButton>
      <MenuList side="bottom" align="start" portal={false}>
        <MenuRadioGroup
          value={activeKey}
          onValueChange={(key) => onChange(key as ChipSourceKey)}
          indicator="none"
        >
          {sources.map((source) => (
            <MenuRadioItem
              key={source.key}
              value={source.key}
              className={styles.sourceOption}
              data-testid="recent-entities-source-option"
            >
              {source.label}
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
      </MenuList>
    </Menu>
  );
};

/**
 * The shortcut entities offered above the selection tree, as chips — the
 * reader's recents, a company's competitors, or the companies a trial plan
 * includes. More than one source turns the heading into a picker.
 *
 * Sources hold more entities than fit collapsed; the "+N" ghost reveals them.
 */
export const RecentEntitiesPanel = ({
  sources,
  defaultSourceKey,
  isSelected,
  onToggle,
  onRemove,
  emptyLabel = 'No recents',
  testId = 'recent-entities-section',
}: {
  /** Ordered; sources with no entities are dropped before anything renders. */
  sources: ChipSource[];
  /** Falls back to the first source left with entities. */
  defaultSourceKey?: string;
  isSelected: (entity: RecentEntity) => boolean;
  /** `source` is the panel's active source, so an add can say where from. */
  onToggle: (
    entity: RecentEntity,
    isSelected: boolean,
    source: ChipSourceKey
  ) => void;
  onRemove?: (entity: RecentEntity) => void;
  emptyLabel?: string;
  testId?: string;
}) => {
  const [chosenKey, setChosenKey] = useState<ChipSourceKey>();
  const [isExpanded, setIsExpanded] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  // A source the reader has emptied — by committing every competitor, say —
  // takes the dropdown entry that was their only way out with it.
  const offered = sources.filter(
    (source) =>
      source.entities.length > 0 || source.keepWhenEmpty || source.isLoading
  );
  const activeSource =
    offered.find((source) => source.key === chosenKey) ??
    offered.find((source) => source.key === defaultSourceKey) ??
    offered[0];

  const selectSource = (key: ChipSourceKey) => {
    setChosenKey(key);
    setIsExpanded(false);
  };

  const entities = activeSource?.entities ?? [];
  const visibleEntities = isExpanded
    ? entities
    : entities.slice(0, COLLAPSED_CHIP_COUNT);
  const hiddenCount = entities.length - visibleEntities.length;
  const isRemovable = Boolean(activeSource?.removable) && Boolean(onRemove);
  const isPending = entities.length === 0 && Boolean(activeSource?.isLoading);
  const isEmpty = entities.length === 0 && !isPending;

  return (
    <div
      className={styles.section}
      data-testid={testId}
      ref={sectionRef}
      tabIndex={-1}
    >
      {activeSource ? (
        <SourceSelect
          sources={offered}
          activeKey={activeSource.key}
          onChange={selectSource}
        />
      ) : (
        <div className={styles.heading}>{sources[0]?.label}</div>
      )}
      {isPending && (
        // Holds the chip row's height so the panel doesn't jump once the
        // source resolves.
        <div className={styles.loadingChips} aria-hidden="true" />
      )}
      {isEmpty && (
        <div className={styles.empty} data-testid="recent-entities-empty">
          {emptyLabel}
        </div>
      )}
      {entities.length > 0 && (
        <div className={styles.chips}>
          {visibleEntities.map((entity) => {
            const selected = isSelected(entity);

            return (
              <span
                key={`${entity.levelId}:${entity.id}`}
                className={classNames(
                  chipStyles.chip,
                  isRemovable && chipStyles.removableChip
                )}
                data-selected={selected ? 'true' : 'false'}
              >
                <button
                  type="button"
                  className={chipStyles.chipToggle}
                  // The chip ellipsizes long names, so the full one has to stay
                  // reachable somewhere.
                  title={entity.name}
                  aria-pressed={selected}
                  data-testid="recent-entity-chip"
                  data-option-label={entity.name}
                  data-option-value={entity.id}
                  onClick={() => {
                    if (activeSource) {
                      onToggle(entity, !selected, activeSource.key);
                    }
                  }}
                >
                  {selected ? (
                    <IconCheckmark2Small className={chipStyles.chipGlyph} />
                  ) : (
                    <IconPlusSmall className={chipStyles.chipGlyph} />
                  )}
                  <span className={chipStyles.chipLabel}>{entity.name}</span>
                </button>
                {/* A chip that is part of the pending selection keeps its slot —
                    dropping it from recents mid-selection would take the
                    checked entity off screen with it. */}
                {isRemovable && !selected && (
                  <button
                    type="button"
                    className={chipStyles.chipRemove}
                    aria-label={`Remove ${entity.name} from recent selections`}
                    data-testid="recent-entity-remove"
                    title={`Remove ${entity.name} from recent selections`}
                    onClick={() => {
                      // Curating a source down to nothing must not bounce the
                      // panel over to another one.
                      setChosenKey(activeSource?.key);
                      onRemove?.(entity);
                      // This button unmounts with its chip. Without somewhere
                      // to send focus it lands on <body>, outside the filter
                      // popover, which then stops answering Escape.
                      sectionRef.current?.focus();
                    }}
                  >
                    <IconCrossSmall className={chipStyles.chipRemoveIcon} />
                  </button>
                )}
              </span>
            );
          })}
          {hiddenCount > 0 && (
            <button
              type="button"
              className={styles.reveal}
              aria-label={`Show ${hiddenCount} more`}
              data-testid="recent-entities-reveal"
              onClick={() => setIsExpanded(true)}
            >
              +{hiddenCount}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
